import React, { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import useFlags from '@/hooks/useFlags';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import LessonOverviewDisplayLocationField from './LessonOverviewDisplayLocationField';
import styles from './CreateTransitionGroup.module.scss';
import formStyles from '../../../../common/Forms/index.module.scss';
import { SetCurrentAction, Slide, SlideGroup, CREATING_SLIDE_GROUPS } from './utils';
import SelectSlideTable from './SelectSlideTable';
import { Footer } from '../../../../common/Modal';
import IconSelect from './IconSelect';
import Spinner from '../../../../common/Spinner';
import Tooltip from '../../../../common/Tooltip';
import { RequiredAsterisk } from '../../../../common/Forms/Utils';
import { ImagePreview } from '../../../../common/Forms';

type EditSlideGroupFormProps = {
  slideGroup: SlideGroup;
  slides: Slide[];
  onSubmit: (any) => void;
  setCurrentAction: SetCurrentAction;
};

type FileFieldComponentProps = {
  accept?: string;
  initialPreview?: string;
  label: string;
  onChange: (file: File | null) => void;
  useImagePreview?: boolean;
  value: any;
};

const FileFieldComponent = ({
  accept, initialPreview, label, onChange, useImagePreview, value,
}: FileFieldComponentProps) => {
  const [imagePreview, setImagePreview] = React.useState(initialPreview);
  const [selectedFileName, setSelectedFileName] = React.useState('');

  React.useEffect(() => {
    if (!initialPreview) return;
    setImagePreview(initialPreview);
  }, [initialPreview]);

  React.useEffect(() => {
    if (value && typeof value.name === 'string') {
      setSelectedFileName(value.name);
    }
  }, [value]);

  const Component = useImagePreview ? 'div' : React.Fragment;
  const componentProps = useImagePreview ? { className: 'tw-flex tw-gap-2 tw-relative' } : {};

  return (
    <div className={formStyles.fieldRow}>
      <label className={formStyles.label} htmlFor="thumbnail_field">
        {label}
      </label>
      <div className={formStyles.fieldColumn}>
        <Component {...componentProps}>
          {selectedFileName === '' && value && (typeof value === 'string' ? value : value.name || 'Selected file')}
          {useImagePreview && <ImagePreview src={imagePreview} />}
          <div className="tw-flex tw-flex-col">
            <input
              accept={accept}
              id="thumbnail_field"
              style={value && selectedFileName === '' ? { color: 'transparent' } : {}}
              type="file"
              onChange={({ target }) => {
                const selectedFile = target.files[0];
                onChange(selectedFile || null);
                if (selectedFile) {
                  setImagePreview(URL.createObjectURL(selectedFile));
                }
                setSelectedFileName(selectedFile ? selectedFile.name : '');
              }}
            />
          </div>
        </Component>
      </div>
    </div>
  );
};

const EditSlideGroupForm = ({ slideGroup, slides, onSubmit, setCurrentAction }: EditSlideGroupFormProps) => {
  const slideIds = slideGroup.slides.map(slide => parseInt(String(slide.id), 10));
  const { displaySlideGroupHeaders, newLessonOverviewPage } = useFlags();

  const slideShow = useSlideShowContext(state => state.slideShow);
  const isAdmin = useSlideShowContext(state => state.isAdmin);

  const slidesForTable = useMemo(() => (
    slides.filter(slide => slide.type !== 'Transition')
  ), [slides]);

  const {
    formState: { isSubmitting },
    handleSubmit,
    register,
    setValue,
    watch,
    control,
  } = useForm({
    defaultValues: {
      display_location_in_lesson_overview: slideGroup.displayLocationInLessonOverview,
      display_slide_header: slideGroup.displaySlideHeader,
      extension: slideGroup.extension,
      icon: slideGroup.icon,
      pacing: slideGroup.pacing,
      slideIds: '',
      thumbnail: {
        lastModified: slideGroup.thumbnailUpdatedAt,
        name: slideGroup.thumbnailFileName,
        size: slideGroup.thumbnailFileSize,
        type: slideGroup.thumbnailContentType,
      },
      title_en: slideGroup.titleEn,
      title_es: slideGroup.titleEs,
    },
  });

  const values = watch();

  return (
    <div className="tw-relative">
      {isSubmitting && (
        <div
          className="tw-absolute tw-inset-0 tw-bg-white tw-bg-opacity-75 tw-flex tw-items-center
          tw-justify-center tw-z-10"
        >
          <Spinner />
        </div>
      )}
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={formStyles.fieldRow}>
          <label className={formStyles.label} htmlFor="title_en_field">
            Title (English)
            <RequiredAsterisk />
          </label>
          <div className={formStyles.fieldColumn}>
            <input
              id="title_en_field"
              placeholder="Enter a title here…"
              type="text"
              {...register('title_en', { required: true })}
            />
          </div>
        </div>

        <div className={formStyles.fieldRow}>
          <label className={formStyles.label} htmlFor="title_es_field">
            Title (Spanish)
            <Tooltip
              content="A Spanish translation will be automatically entered if there is none provided."
              size="medium"
              theme="white"
            >
              <span>
                <i aria-hidden className="fa fa-question-circle" />
                <span className="sr-only">
                  A Spanish translation will be automatically entered if there is none provided.
                </span>
              </span>
            </Tooltip>
          </label>
          <div className={formStyles.fieldColumn}>
            <input
              id="title_es_field"
              placeholder="Enter a title here…"
              type="text"
              {...register('title_es')}
            />
          </div>
        </div>

        <div className="tw-flex tw-w-3/12 tw-justify-between">
          <div className={formStyles.fieldRow}>
            <label className={formStyles.label} htmlFor="pacing_field">
              Pacing Time (minutes)
            </label>
            <div className={formStyles.fieldColumn}>
              <input
                className={styles.numberInput}
                id="pacing_field"
                placeholder="Enter time..."
                type="text"
                {...register('pacing')}
              />
            </div>
          </div>

          <div className={formStyles.fieldRow}>
            <div className={formStyles.fieldColumn}>
              <label className="tw-mr-2 tw-font-bold tw-inline-flex tw-items-center" htmlFor="extension_field">
                <span>
                  Extension Activity
                  <Tooltip
                    content="Enabling this will label this activity as an extension in the transition slides."
                    size="medium"
                    theme="white"
                  >
                    <span>
                      <i aria-hidden className="fa fa-question-circle" />
                      <span className="sr-only">
                        Enabling this will label this activity as an extension in the transition slides.
                      </span>
                    </span>
                  </Tooltip>
                </span>
                <input
                  className="tw-ml-2"
                  id="extension_field"
                  type="checkbox"
                  {...register('extension')}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Hide checkbox for non-admins and non tci slide shows */}
        {newLessonOverviewPage && isAdmin && slideShow.tciSlideShow && (
          <>
            <div className="tw-flex tw-flex-shrink-0 tw-h-14">
              <LessonOverviewDisplayLocationField
                setFormValue={setValue}
                values={values}
              />
            </div>
            {values.display_location_in_lesson_overview === 'lesson_options' && (
              <div className="tw-flex tw-flex-shrink-0 tw-h-14">
                <Controller
                  control={control}
                  name="thumbnail"
                  render={({ field }) => (
                    <FileFieldComponent
                      useImagePreview
                      accept="image/png, image/jpg, image/jpeg, image/gif"
                      initialPreview={slideGroup.thumbnail}
                      label="Activity Image"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            )}
          </>
        )}

        {displaySlideGroupHeaders && (
          <div className="tw-flex tw-w-2/5">
            <div className="tw-flex tw-w-3/12 tw-justify-between">
              <div className={formStyles.fieldRow}>
                <div className={formStyles.fieldColumn}>
                  <label
                    className="tw-mr-2 tw-font-bold tw-inline-flex tw-items-center"
                    htmlFor="display_slide_header_field"
                  >
                    <span>
                      Display Slide Header
                      <Tooltip
                        content="Enabling this will display the Slide Group title on each slide in this group."
                        size="medium"
                        theme="white"
                      >
                        <span>
                          <i aria-hidden className="fa fa-question-circle" />
                          <span className="sr-only">
                            Enabling this will display the Slide Group title on each slide in this group.
                          </span>
                        </span>
                      </Tooltip>
                    </span>
                    <input
                      className="tw-ml-2"
                      id="display_slide_header_field"
                      type="checkbox"
                      {...register('display_slide_header')}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="tw-flex tw-w-3/12">
              <IconSelect
                currentIcon={slideGroup.icon}
                setIcon={setValue}
              />
            </div>
          </div>
        )}

        <input
          id="slideIds"
          placeholder="Slide Ids"
          type="hidden"
          {...register('slideIds')}
        />

        <div className={styles.table}>
          <SelectSlideTable
            initialSelectedSlideIds={slideIds}
            setValue={setValue}
            slides={slidesForTable}
          />
        </div>

        <Footer
          nearlyFullScreen
          secondaryButtonCallback={() => {
            setCurrentAction(CREATING_SLIDE_GROUPS);
          }}
          secondaryButtonText="Back"
          wrapperClassName="w100"
        />
      </form>
    </div>
  );
};

export default EditSlideGroupForm;
